import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Edit2, Check, X, Loader2, ChevronDown, ChevronRight, RefreshCw, Layers, Image, ExternalLink, Eye, MousePointer, DollarSign, FileImage } from 'lucide-react';

export default function MetaAdsCampaignsLab() {
  const { selectedBrand } = useAuth();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState(null);
  
  const [updatingId, setUpdatingId] = useState(null);
  const [editBudget, setEditBudget] = useState(null);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  
  // Expandable state
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedAdsets, setExpandedAdsets] = useState({});
  
  // Loading states for lazy loading
  const [loadingAdsets, setLoadingAdsets] = useState({});
  const [loadingAds, setLoadingAds] = useState({});
  
  // Ad Detail Modal
  const [selectedAd, setSelectedAd] = useState(null);
  const [imageError, setImageError] = useState(false);
  
  // Toggle campaign and fetch adsets if needed
  const toggleCampaign = async (campaignId) => {
    const isExpanding = !expandedCampaigns[campaignId];
    setExpandedCampaigns(prev => ({ ...prev, [campaignId]: isExpanding }));
    
    // If expanding and adsets not loaded yet, fetch them
    if (isExpanding) {
      const campaign = campaigns.find(c => c.id === campaignId);
      if (campaign && campaign.adsets === null) {
        await fetchAdsets(campaignId);
      }
    }
  };
  
  // Toggle adset and fetch ads if needed
  const toggleAdset = async (adsetId, campaignId) => {
    const isExpanding = !expandedAdsets[adsetId];
    setExpandedAdsets(prev => ({ ...prev, [adsetId]: isExpanding }));
    
    // If expanding and ads not loaded yet, fetch them
    if (isExpanding) {
      const campaign = campaigns.find(c => c.id === campaignId);
      const adset = campaign?.adsets?.find(a => a.id === adsetId);
      if (adset && adset.ads === null) {
        await fetchAds(adsetId, campaignId);
      }
    }
  };

  useEffect(() => {
    if (selectedBrand?.id) {
      fetchCampaigns();
    }
  }, [selectedBrand?.id]);

  // Fetch only campaigns (fast)
  const fetchCampaigns = async () => {
    if (!selectedBrand?.id) return;

    setLoading(true);
    setError(null);
    setCampaigns([]);
    setExpandedCampaigns({});
    setExpandedAdsets({});

    try {
      const response = await fetch('https://api.glowytics.com/api/labs/meta-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ mode: 'campaigns' }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch campaigns');
      }

      if (data.success && Array.isArray(data.campaigns)) {
        setCampaigns(data.campaigns);
        toast.success(`Found ${data.campaigns.length} active campaigns`);
      } else {
        throw new Error(data.error || 'Invalid response format');
      }

    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch adsets for a campaign (lazy)
  const fetchAdsets = async (campaignId) => {
    setLoadingAdsets(prev => ({ ...prev, [campaignId]: true }));
    
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/meta-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ mode: 'adsets', entityId: campaignId }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success && Array.isArray(data.adsets)) {
        // Update campaigns state with loaded adsets
        setCampaigns(prev => prev.map(c => 
          c.id === campaignId ? { ...c, adsets: data.adsets } : c
        ));
      }
    } catch (err) {
      console.error('Fetch adsets error:', err);
      toast.error('Failed to load ad sets');
    } finally {
      setLoadingAdsets(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  // Fetch ads for an adset (lazy)
  const fetchAds = async (adsetId, campaignId) => {
    setLoadingAds(prev => ({ ...prev, [adsetId]: true }));
    
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/meta-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ mode: 'ads', entityId: adsetId }),
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success && Array.isArray(data.ads)) {
        // Update campaigns state with loaded ads
        setCampaigns(prev => prev.map(c => {
          if (c.id !== campaignId) return c;
          return {
            ...c,
            adsets: c.adsets?.map(a => 
              a.id === adsetId ? { ...a, ads: data.ads } : a
            )
          };
        }));
      }
    } catch (err) {
      console.error('Fetch ads error:', err);
      toast.error('Failed to load ads');
    } finally {
      setLoadingAds(prev => ({ ...prev, [adsetId]: false }));
    }
  };

  const updateEntity = async (entityId, field, value, campaignIdToUpdateInState) => {
    setUpdatingId(campaignIdToUpdateInState);
    try {
      const response = await fetch('https://api.glowytics.com/api/labs/meta-ads/campaigns-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({
          entityId,
          field,
          value
        }),
        credentials: 'include'
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.details?.error || 'Update failed');
      }

      if (data.success) {
        toast.success('Updated successfully');
        setCampaigns(prev => prev.map(c => {
          if (c.id === campaignIdToUpdateInState) {
            if (field === 'status') {
              return { ...c, status: value };
            } else if (field === 'daily_budget') {
              return { ...c, budget: parseInt(value) / 100 }; // Meta usually uses cents
            }
          }
          return c;
        }));
      }

    } catch (err) {
      console.error('Update error:', err);
      toast.error(err.message);
    } finally {
      setUpdatingId(null);
      setEditBudget(null);
      setOpenStatusDropdown(null);
    }
  };

  const handleSaveBudget = (campaign) => {
    if (!editBudget || editBudget.id !== campaign.id) return;
    
    // Convert to cents (Meta standard)
    const cents = Math.round(parseFloat(editBudget.value) * 100);
    if (isNaN(cents) || cents <= 0) {
      toast.error('Invalid budget amount');
      return;
    }

    updateEntity(campaign.id, 'daily_budget', cents, campaign.id);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meta Ads Campaign Manager</h1>
          <p className="text-gray-500 mt-1">View active campaigns with their Ad Sets and Ads. Click to expand hierarchy.</p>
        </div>
        <button
          onClick={fetchCampaigns}
          disabled={loading}
          className={`p-2 rounded-lg transition-colors border ${
            loading 
              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'
          }`}
          title="Refresh Data"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && campaigns.length === 0 && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-500">No campaigns found.</p>
        </div>
      )}

      {campaigns.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type / Goal</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Spend</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Impr. / Clicks</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                    {/* Campaign Row */}
                    <tr className="hover:bg-gray-50 bg-gray-50/50">
                    <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => toggleCampaign(campaign.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            {expandedCampaigns[campaign.id] ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                          <div>
                            <div className="font-semibold text-gray-900">{campaign.name}</div>
                            <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
                              <span>{campaign.id}</span>
                              <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                {campaign.adsets?.length || 0} Ad Sets
                              </span>
                            </div>
                          </div>
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap relative">
                      <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenStatusDropdown(openStatusDropdown === campaign.id ? null : campaign.id);
                          }}
                        disabled={updatingId === campaign.id}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none ${updatingId === campaign.id ? 'opacity-50 cursor-wait' : ''}`}
                      >
                         {updatingId === campaign.id ? (
                           <Loader2 className="w-3 h-3 animate-spin text-gray-500" />
                         ) : (
                           <div className={`w-2.5 h-2.5 rounded-full ${campaign.status === 'ENABLED' ? 'bg-green-500 shadow-[0_0_0_2px_rgba(34,197,94,0.2)]' : 'bg-gray-400'}`} />
                         )}
                         <span className={`text-sm font-medium ${campaign.status === 'ENABLED' ? 'text-gray-900' : 'text-gray-500'}`}>
                            {campaign.status === 'ENABLED' ? 'Active' : 'Paused'}
                         </span>
                         <ChevronDown className="w-3 h-3 text-gray-400" />
                      </button>

                      {openStatusDropdown === campaign.id && (
                        <>
                          <div className="fixed inset-0 z-10 cursor-default" onClick={() => setOpenStatusDropdown(null)} />
                          <div className="absolute top-full left-0 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
                             <button
                                onClick={() => updateEntity(campaign.id, 'status', 'ACTIVE', campaign.id)}
                                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                  ${campaign.status === 'ENABLED' ? 'text-green-600 bg-green-50/50' : 'text-gray-700'}
                                `}
                             >
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                <span>Enable</span>
                                {campaign.status === 'ENABLED' && <Check className="w-3.5 h-3.5 ml-auto" />}
                             </button>
                             <button
                                onClick={() => updateEntity(campaign.id, 'status', 'PAUSED', campaign.id)}
                                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors
                                  ${campaign.status === 'PAUSED' || campaign.status !== 'ENABLED' ? 'text-gray-900 bg-gray-50' : 'text-gray-700'}
                                `}
                             >
                                <div className="w-2 h-2 rounded-full bg-gray-400" />
                                <span>Pause</span>
                                {(campaign.status === 'PAUSED' || campaign.status !== 'ENABLED') && <Check className="w-3.5 h-3.5 ml-auto" />}
                             </button>
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">{campaign.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {campaign.budget_type === 'lifetime' ? (
                         <div className="text-sm text-gray-500">
                            {campaign.budget ? campaign.budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                            <span className="text-xs ml-1">(Lifetime)</span>
                         </div>
                      ) : (
                        editBudget && editBudget.id === campaign.id ? (
                            <div className="flex items-center justify-end gap-2">
                            <input
                                type="number"
                                value={editBudget.value}
                                onChange={(e) => setEditBudget({ ...editBudget, value: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                autoFocus
                            />
                            <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveBudget(campaign);
                                }}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                            >
                                <Check className="w-4 h-4" />
                            </button>
                            <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditBudget(null);
                                }}
                                className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            </div>
                        ) : (
                            <div className="flex items-center justify-end gap-2 group">
                            <span className="text-sm text-gray-900 font-medium">
                                {campaign.budget ? campaign.budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                            </span>
                            <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditBudget({ id: campaign.id, value: campaign.budget });
                                }}
                                disabled={updatingId === campaign.id}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition-opacity"
                            >
                                <Edit2 className="w-3 h-3" />
                            </button>
                            </div>
                        )
                      )}
                    </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 font-medium">
                      {campaign.cost ? campaign.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                      <div>{campaign.impressions?.toLocaleString()} <span className="text-xs text-gray-400">Imp</span></div>
                      <div>{campaign.clicks?.toLocaleString()} <span className="text-xs text-gray-400">Clicks</span></div>
                    </td>
                  </tr>

                    {/* Ad Sets Loading */}
                    {expandedCampaigns[campaign.id] && loadingAdsets[campaign.id] && (
                      <tr className="bg-blue-50/30">
                        <td colSpan="6" className="px-6 py-4 pl-12">
                          <div className="flex items-center gap-2 text-blue-600">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Loading ad sets...</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    {/* Ad Sets Empty */}
                    {expandedCampaigns[campaign.id] && !loadingAdsets[campaign.id] && campaign.adsets !== null && campaign.adsets?.length === 0 && (
                      <tr className="bg-blue-50/30">
                        <td colSpan="6" className="px-6 py-4 pl-12">
                          <span className="text-sm text-gray-500">No ad sets found</span>
                        </td>
                      </tr>
                    )}
                    
                    {/* Ad Sets */}
                    {expandedCampaigns[campaign.id] && !loadingAdsets[campaign.id] && campaign.adsets?.map((adset) => (
                      <React.Fragment key={adset.id}>
                        {/* Ad Set Row */}
                        <tr className="hover:bg-blue-50/30 bg-white border-l-4 border-l-blue-200">
                          <td className="px-6 py-3 pl-12">
                            <div className="flex items-center gap-2">
                              <button 
                                onClick={() => toggleAdset(adset.id, campaign.id)}
                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                              >
                                {loadingAds[adset.id] ? (
                                  <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                                ) : expandedAdsets[adset.id] ? (
                                  <ChevronDown className="w-4 h-4 text-blue-500" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-blue-500" />
                                )}
                              </button>
                              <Layers className="w-4 h-4 text-blue-500" />
                              <div>
                                <div className="font-medium text-gray-800">{adset.name}</div>
                                <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
                                  <span>{adset.id}</span>
                                  {adset.ads !== null && (
                                    <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-[10px] font-medium">
                                      {adset.ads?.length || 0} Ads
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${adset.status === 'ENABLED' || adset.status === 'ACTIVE' ? 'bg-green-500' : 'bg-gray-400'}`} />
                              <span className={`text-sm ${adset.status === 'ENABLED' || adset.status === 'ACTIVE' ? 'text-gray-800' : 'text-gray-500'}`}>
                                {adset.status === 'ENABLED' || adset.status === 'ACTIVE' ? 'Active' : adset.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-gray-500 text-sm">
                            {adset.optimization_goal || '-'}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                            {adset.budget ? adset.budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-700">
                            {adset.cost ? adset.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                            <div>{adset.impressions?.toLocaleString() || 0}</div>
                            <div>{adset.clicks?.toLocaleString() || 0}</div>
                          </td>
                        </tr>

                        {/* Ads Loading */}
                        {expandedAdsets[adset.id] && loadingAds[adset.id] && (
                          <tr className="bg-purple-50/30">
                            <td colSpan="6" className="px-6 py-3 pl-20">
                              <div className="flex items-center gap-2 text-purple-600">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm">Loading ads...</span>
                              </div>
                            </td>
                          </tr>
                        )}
                        
                        {/* Ads Empty */}
                        {expandedAdsets[adset.id] && !loadingAds[adset.id] && adset.ads !== null && adset.ads?.length === 0 && (
                          <tr className="bg-purple-50/30">
                            <td colSpan="6" className="px-6 py-3 pl-20">
                              <span className="text-sm text-gray-500">No ads found</span>
                            </td>
                          </tr>
                        )}
                        
                        {/* Ads */}
                        {expandedAdsets[adset.id] && !loadingAds[adset.id] && adset.ads?.map((ad) => (
                          <tr 
                            key={ad.id} 
                            className="hover:bg-purple-50/50 bg-white border-l-4 border-l-purple-200 cursor-pointer transition-colors"
                            onClick={() => { setImageError(false); setSelectedAd(ad); }}
                          >
                            <td className="px-6 py-2.5 pl-20">
                              <div className="flex items-center gap-2">
                                {ad.creative?.image_url ? (
                                  <img 
                                    src={ad.creative.image_url} 
                                    alt="" 
                                    className="w-8 h-8 rounded object-cover border border-gray-200"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.style.display = 'none';
                                      e.target.nextSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : null}
                                <div className={`w-8 h-8 rounded bg-purple-100 flex items-center justify-center ${ad.creative?.image_url ? 'hidden' : ''}`}>
                                  <FileImage className="w-4 h-4 text-purple-500" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-700 text-sm">{ad.name}</div>
                                  <div className="text-xs text-gray-400">{ad.id}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-2.5 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${
                                  ad.effective_status === 'ACTIVE' ? 'bg-green-500' : 
                                  ad.effective_status === 'PAUSED' ? 'bg-gray-400' :
                                  ad.effective_status === 'PENDING_REVIEW' ? 'bg-yellow-500' :
                                  ad.effective_status === 'DISAPPROVED' ? 'bg-red-500' : 'bg-gray-400'
                                }`} />
                                <span className={`text-xs ${
                                  ad.effective_status === 'ACTIVE' ? 'text-gray-700' : 'text-gray-500'
                                }`}>
                                  {ad.effective_status || ad.status}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-2.5 whitespace-nowrap text-gray-400 text-xs">Ad</td>
                            <td className="px-6 py-2.5 whitespace-nowrap text-right text-xs text-gray-400">-</td>
                            <td className="px-6 py-2.5 whitespace-nowrap text-right text-sm text-gray-600">
                              {ad.cost ? ad.cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                            </td>
                            <td className="px-6 py-2.5 whitespace-nowrap text-right text-xs text-gray-500">
                              <div>{ad.impressions?.toLocaleString() || 0}</div>
                              <div>{ad.clicks?.toLocaleString() || 0}</div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ad Detail Modal */}
      <AnimatePresence>
        {selectedAd && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedAd(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">{selectedAd.name}</h3>
                  <p className="text-purple-200 text-sm">Ad ID: {selectedAd.id}</p>
                </div>
                <button 
                  onClick={() => setSelectedAd(null)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left: Creative Preview */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Creative Preview</h4>
                    
                    {selectedAd.creative?.image_url && !imageError ? (
                      <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-gray-50">
                        <img 
                          src={selectedAd.creative.image_url} 
                          alt="Ad Creative" 
                          className="w-full h-auto max-h-80 object-contain"
                          onError={() => setImageError(true)}
                        />
                      </div>
                    ) : (
                      <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-12 flex flex-col items-center justify-center">
                        <FileImage className="w-12 h-12 text-gray-300 mb-3" />
                        <p className="text-gray-400 text-sm">No preview available</p>
                      </div>
                    )}

                    {/* Performance Metrics */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-blue-50 rounded-xl p-4 text-center">
                        <Eye className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                        <div className="text-lg font-bold text-gray-900">
                          {selectedAd.impressions?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-gray-500">Impressions</div>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4 text-center">
                        <MousePointer className="w-5 h-5 text-green-500 mx-auto mb-1" />
                        <div className="text-lg font-bold text-gray-900">
                          {selectedAd.clicks?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-gray-500">Clicks</div>
                      </div>
                      <div className="bg-purple-50 rounded-xl p-4 text-center">
                        <DollarSign className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                        <div className="text-lg font-bold text-gray-900">
                          {selectedAd.cost?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0'}
                        </div>
                        <div className="text-xs text-gray-500">Spend</div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Ad Copy */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Ad Copy</h4>
                    
                    <div className="space-y-4">
                      {/* Headline */}
                      {selectedAd.creative?.title && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Headline</label>
                          <p className="text-gray-900 font-semibold mt-1">{selectedAd.creative.title}</p>
                        </div>
                      )}

                      {/* Primary Text / Body */}
                      {selectedAd.creative?.body && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Primary Text</label>
                          <p className="text-gray-700 mt-1 whitespace-pre-wrap">{selectedAd.creative.body}</p>
                        </div>
                      )}

                      {/* Description */}
                      {selectedAd.creative?.description && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description</label>
                          <p className="text-gray-600 mt-1">{selectedAd.creative.description}</p>
                        </div>
                      )}

                      {/* CTA */}
                      {selectedAd.creative?.cta_type && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Call to Action</label>
                          <span className="inline-block mt-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            {selectedAd.creative.cta_type.replace(/_/g, ' ')}
                          </span>
                        </div>
                      )}

                      {/* Link URL */}
                      {selectedAd.creative?.link_url && (
                        <div className="bg-gray-50 rounded-xl p-4">
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Destination URL</label>
                          <a 
                            href={selectedAd.creative.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mt-1 text-sm break-all"
                          >
                            <ExternalLink className="w-4 h-4 flex-shrink-0" />
                            {selectedAd.creative.link_url}
                          </a>
                        </div>
                      )}

                      {/* Status */}
                      <div className="bg-gray-50 rounded-xl p-4">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</label>
                        <div className="flex items-center gap-2 mt-1">
                          <div className={`w-2.5 h-2.5 rounded-full ${
                            selectedAd.effective_status === 'ACTIVE' ? 'bg-green-500' : 
                            selectedAd.effective_status === 'PAUSED' ? 'bg-gray-400' :
                            selectedAd.effective_status === 'PENDING_REVIEW' ? 'bg-yellow-500' :
                            selectedAd.effective_status === 'DISAPPROVED' ? 'bg-red-500' : 'bg-gray-400'
                          }`} />
                          <span className="text-gray-700 font-medium">{selectedAd.effective_status || selectedAd.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

